import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  ClipboardCopy,
  Loader2,
  Lock,
  Mail,
  Users,
  X,
} from 'lucide-react';
import { recordSupportBulkOutreach, shareableLink } from '../../Services/SupportService';
import {
  SUPPORT_CHANNEL_LABELS,
  SupportBulkOutreachResult,
  SupportChannelKey,
  SupportLink,
} from '../../Types/SupportType';

interface SupportBulkOutreachModalProps {
  channel: SupportChannelKey;
  /** The distinct visitor ids currently selected. Non-empty when this modal is open. */
  visitorIds: string[];
  links: SupportLink[];
  onClose: () => void;
  onCompleted: (result: SupportBulkOutreachResult) => void;
  onError: (title: string, message: string) => void;
}

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50';

const DEFAULT_COPY =
  'Hi - you visited Nexgent but did not finish creating your profile. It takes about two minutes and your progress is saved. Here is the link: {link}';

/**
 * Records one nudge for every selected visitor before rendering a summary.
 *
 * The channel decides what the backend does with each row - not this component. Email is the only
 * channel with a real transmission path, so an email bulk sends where an address exists and
 * records the rest; every other channel records {SHARED} because there is no authorised way for
 * Nexgent to message the visitor at all. The button therefore says "share" on non-email channels
 * and "send" on email, and the confirmation screen shows the composed message ready to paste.
 */
const SupportBulkOutreachModal = ({
  channel,
  visitorIds,
  links,
  onClose,
  onCompleted,
  onError,
}: SupportBulkOutreachModalProps) => {
  const [linkId, setLinkId] = useState<string>('');
  const [message, setMessage] = useState<string>(DEFAULT_COPY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SupportBulkOutreachResult | null>(null);
  const [copied, setCopied] = useState(false);

  const open = visitorIds.length > 0;

  // Only links filed under this channel, and only active ones - sharing an archived link would
  // point every visitor at a URL the owner has already retired.
  const channelLinks = useMemo(
    () => links.filter((link) => link.channel === channel && link.active),
    [links, channel]
  );

  const selectedLink = useMemo(
    () => channelLinks.find((link) => link.id === linkId) ?? null,
    [channelLinks, linkId]
  );

  // Adopt the selected link's own template the first time it is chosen.
  useEffect(() => {
    if (selectedLink?.messageTemplate) {
      setMessage(selectedLink.messageTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink?.id]);

  const preview = useMemo(() => {
    if (!selectedLink) return message;
    // The link travels as one clean value - the display name when it is a full URL, otherwise
    // the short link - so the recipient sees exactly what "Ready to share" shows, and a text
    // name still routes through the short link to the tracked destination.
    const shareText = shareableLink(selectedLink);
    if (!shareText) return message;
    return message.includes('{link}')
      ? message.replace(/\{link\}/g, shareText)
      : `${message}\n\n${shareText}`;
  }, [message, selectedLink]);

  const handleSubmit = async () => {
    if (!linkId) return;
    setSubmitting(true);
    try {
      const response = await recordSupportBulkOutreach({
        linkId: selectedLink?.id ?? linkId,
        channel,
        message,
        visitorIds,
      });
      if (response.success && response.data) {
        setResult(response.data);
        onCompleted(response.data);
      } else {
        onError('Could not record', response.message || 'Please try again.');
      }
    } catch {
      onError('Could not record', 'Failed to record the bulk message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onError('Copy failed', 'Unable to copy to the clipboard.');
    }
  };

  const emailChannel = channel === 'email';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !submitting && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-100">
                  {emailChannel ? 'Send' : 'Share'} with {visitorIds.length} visitors
                </h2>
                <p className="text-xs font-semibold text-slate-400">
                  {SUPPORT_CHANNEL_LABELS[channel]} · one message, recorded per visitor
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {result ? (
              <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-center">
                    <div className="text-lg font-black tabular-nums text-slate-100">
                      {result.total.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                      Total
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-center">
                    <div className="text-lg font-black tabular-nums text-emerald-400">
                      {result.sent.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-emerald-600">
                      Sent
                    </div>
                  </div>
                  <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-center">
                    <div className="text-lg font-black tabular-nums text-cyan-400">
                      {result.shared.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-cyan-600">
                      Shared
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-center">
                    <div className="text-lg font-black tabular-nums text-slate-300">
                      {result.logged.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-600">
                      Logged
                    </div>
                  </div>
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-center">
                    <div className="text-lg font-black tabular-nums text-rose-400">
                      {result.failed.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-rose-600">
                      Failed
                    </div>
                  </div>
                </div>

                {result.failures.length > 0 && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                    <p className="text-xs font-black uppercase tracking-wider text-rose-300">
                      Could not record {result.failures.length}
                    </p>
                    <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                      {result.failures.map((failure) => (
                        <p key={failure.visitorId} className="text-[11px] font-semibold text-rose-200">
                          <span className="font-mono">{failure.visitorId}</span> — {failure.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className={labelClass}>Message</label>
                    <button
                      onClick={copyPreview}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
                    >
                      {copied ? <Check size={12} /> : <ClipboardCopy size={12} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-xs font-semibold text-slate-300">
                    {preview}
                  </div>
                </div>

                {!emailChannel && (
                  <p className="flex items-start gap-2 text-[11px] font-semibold text-amber-400">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    {SUPPORT_CHANNEL_LABELS[channel]} has no provider, so nothing was transmitted.
                    These were recorded as shared so the follow-up queue knows the link reached
                    them. Copy the message and post it on {SUPPORT_CHANNEL_LABELS[channel]} yourself.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                {emailChannel ? (
                  <div className="flex items-start gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3">
                    <Mail size={15} className="mt-0.5 shrink-0 text-cyan-400" />
                    <p className="text-xs font-semibold text-cyan-100">
                      Where an email address is on file, Nexgent emails the visitor. Everyone else
                      is recorded as shared rather than &quot;sent&quot; - the audit trail stays honest.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                    <Lock size={15} className="mt-0.5 shrink-0 text-amber-400" />
                    <p className="text-xs font-semibold text-amber-200">
                      Nexgent has no {SUPPORT_CHANNEL_LABELS[channel]} integration. Copy the message
                      and post it on {SUPPORT_CHANNEL_LABELS[channel]} yourself; this records that
                      the link was shared with these visitors.
                    </p>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Tracking link</label>
                  <select value={linkId} onChange={(e) => setLinkId(e.target.value)} className={inputClass}>
                    <option value="">Select a link…</option>
                    {channelLinks.map((link) => (
                      <option key={link.id} value={link.id}>
                        {link.displayName} · {link.visitors.toLocaleString()} visitors
                      </option>
                    ))}
                  </select>
                  {channelLinks.length === 0 && (
                    <p className="mt-1 text-[10px] font-semibold text-slate-600">
                      No active {SUPPORT_CHANNEL_LABELS[channel]} links yet. Create one on the
                      Generate Link tab to attribute the follow-up.
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={7}
                    className={`${inputClass} resize-y`}
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-600">
                    {'{link}'} is replaced with the display name as a hyperlink, e.g. [test](url).
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              {result ? (
                <button
                  onClick={onClose}
                  className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !linkId || !channelLinks.length}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : emailChannel ? (
                      <Mail size={16} />
                    ) : (
                      <Users size={16} />
                    )}
                    {submitting
                      ? 'Recording…'
                      : emailChannel
                        ? `Send & record ${visitorIds.length}`
                        : `Share with ${visitorIds.length}`}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SupportBulkOutreachModal;